import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { CHECKIN_MAX_AI_FRAMES } from "@/lib/walkaround";
import type { AiReport, CheckinFrame, CheckinVideo } from "@/types";

interface StartInput {
  phase: string;
  recorderRole: string;
}

interface FinishInput {
  phase: string;
  recorderRole: string;
  frames: CheckinFrame[];
  durationSeconds: number;
  note: string;
  videoPath: string;
  videoMime: string;
  videoSize: number;
}

interface InspectContext {
  vehicle: string;
  viewer: "renter" | "host";
}

export function useCheckins(bookingId: number | null, vehicleId: number | null) {
  const { user, profile } = useAuth();
  const [checkins, setCheckins] = useState<CheckinVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [inspectingId, setInspectingId] = useState<number | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (!bookingId) {
      setCheckins([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error: fetchError } = await supabase
        .from("checkin_videos")
        .select("*")
        .eq("booking_id", bookingId)
        .order("created_at", { ascending: false });
      if (fetchError) throw fetchError;
      if (mounted.current) {
        // Cancelled and in-progress (recording) rows stay hidden from the list.
        setCheckins(((data as CheckinVideo[]) ?? []).filter((item) => item.status !== "cancelled" && item.status !== "recording"));
        setError(null);
      }
    } catch (err) {
      if (mounted.current) {
        setError(err instanceof Error ? err.message : "We couldn't load the check-in videos.");
      }
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    load();
  }, [load]);

  /** Create a placeholder row the moment filming starts — this drives the live ping. */
  const startRecording = useCallback(
    async ({ phase, recorderRole }: StartInput): Promise<number | null> => {
      if (!bookingId) return null;
      try {
        const { data, error: insertError } = await supabase
          .from("checkin_videos")
          .insert({
            booking_id: bookingId,
            vehicle_id: vehicleId,
            recorded_by: user?.id ?? null,
            recorder_role: recorderRole,
            recorder_name: profile?.full_name || user?.email || "Gari host",
            phase,
            status: "recording",
            frames: [],
          })
          .select("id")
          .single();
        if (insertError) throw insertError;
        return (data as { id: number }).id;
      } catch {
        // The ping is a nice-to-have — never block filming on it.
        return null;
      }
    },
    [bookingId, vehicleId, user, profile]
  );

  const finishRecording = useCallback(
    async (recordingId: number | null, input: FinishInput) => {
      setSaving(true);
      setError(null);
      try {
        const payload = {
          phase: input.phase,
          frames: input.frames,
          duration_seconds: input.durationSeconds,
          note: input.note.trim() || null,
          video_path: input.videoPath,
          video_mime: input.videoMime,
          video_size: input.videoSize,
          status: "ready",
        };
        if (recordingId) {
          const { data, error: updateError } = await supabase
            .from("checkin_videos")
            .update(payload)
            .eq("id", recordingId)
            .select("*")
            .single();
          if (updateError) throw updateError;
          if (mounted.current) {
            setCheckins((prev) => [data as CheckinVideo, ...prev.filter((item) => item.id !== recordingId)]);
          }
          return data as CheckinVideo;
        }
        const { data, error: insertError } = await supabase
          .from("checkin_videos")
          .insert({
            booking_id: bookingId,
            vehicle_id: vehicleId,
            recorded_by: user?.id ?? null,
            recorder_role: input.recorderRole,
            recorder_name: profile?.full_name || user?.email || "Gari host",
            ...payload,
          })
          .select("*")
          .single();
        if (insertError) throw insertError;
        if (mounted.current) setCheckins((prev) => [data as CheckinVideo, ...prev]);
        return data as CheckinVideo;
      } catch (err) {
        setError(err instanceof Error ? err.message : "The walkaround couldn't be saved.");
        throw err;
      } finally {
        if (mounted.current) setSaving(false);
      }
    },
    [bookingId, vehicleId, user, profile]
  );

  const cancelRecording = useCallback(async (recordingId: number | null) => {
    if (!recordingId) return;
    try {
      await supabase.from("checkin_videos").update({ status: "cancelled" }).eq("id", recordingId);
    } catch {
      // Leaving the row in "recording" would just age out of the ping window.
    }
  }, []);

  /** Ask the AI to inspect the walkaround stills for faults or damage. */
  const inspect = useCallback(
    async (checkin: CheckinVideo, context: InspectContext): Promise<AiReport> => {
      setInspectingId(checkin.id);
      try {
        const images = (checkin.frames ?? []).slice(0, CHECKIN_MAX_AI_FRAMES).map((frame) => frame.url);
        if (images.length === 0) throw new Error("This clip has no stills to inspect.");

        const { data, error: invokeError } = await supabase.functions.invoke("detect-walkaround-faults", {
          body: {
            images,
            phase: checkin.phase,
            vehicle: context.vehicle,
            note: checkin.note,
          },
        });
        if (invokeError) {
          let message = invokeError.message || "The AI inspection couldn't run.";
          try {
            const body = await (invokeError as unknown as { context?: Response }).context?.json?.();
            if (body?.error) message = body.error;
          } catch {
            // keep the generic message
          }
          throw new Error(message);
        }
        if (data?.error) throw new Error(data.error);

        const report = data as AiReport;
        const { data: updated } = await supabase
          .from("checkin_videos")
          .update({ ai_report: report, ai_checked_at: new Date().toISOString() })
          .eq("id", checkin.id)
          .select("*")
          .maybeSingle();
        if (updated && mounted.current) {
          setCheckins((prev) => prev.map((item) => (item.id === checkin.id ? (updated as CheckinVideo) : item)));
        }
        return report;
      } finally {
        if (mounted.current) setInspectingId(null);
      }
    },
    []
  );

  return {
    checkins,
    loading,
    error,
    saving,
    inspectingId,
    startRecording,
    finishRecording,
    cancelRecording,
    inspect,
    reload: load,
  };
}